'use client'

import * as React from 'react'
import { cn } from '../../helpers/index.js'
import ProductCard from './ProductCard.js'
import { Heading, Text } from '../../components/index.js'
import type { BuyOneGetOneShelfProps } from './types.js'
import { useExposure } from '../../hooks/useExposure.js'

const componentType = 'shelf'
const componentName = 'buy_one_get_one_shelf'

/**
 * BuyOneGetOneShelfRoot - 买赠货架根容器组件
 *
 * @description 组合式设计的买赠货架容器，负责布局和标题展示
 * 支持传入多个 BuyOneGetOneShelf.ProductCard 子组件
 */
const BuyOneGetOneShelfRoot = React.forwardRef<HTMLDivElement, BuyOneGetOneShelfProps>(
  ({ className, classNames = {}, theme, title, subtitle, children, ...props }, ref) => {
    const boxRef = React.useRef<HTMLDivElement>(null)

    useExposure(boxRef, {
      componentType,
      componentName,
      componentTitle: title,
      componentDescription: subtitle,
    })

    React.useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

    return (
      <div
        ref={boxRef}
        className={cn(
          'bogo-shelf-root w-full py-0',
          {
            'aiui-dark': theme === 'dark',
          },
          className,
          classNames?.root
        )}
        {...props}
      >
        {title && (
          <Heading as="h2" html={title} className={cn('bogo-shelf-title text-info-primary mb-4', classNames?.title)} />
        )}
        {subtitle && (
          <Text
            size={3}
            as="p"
            html={subtitle}
            className={cn('bogo-shelf-description text-info-primary mb-6', classNames?.description)}
          />
        )}
        {/* 买赠货架产品区域 */}
        <div className={cn('flex flex-wrap justify-center gap-6', 'laptop:justify-start laptop:flex-row')}>
          {children}
        </div>
      </div>
    )
  }
)

BuyOneGetOneShelfRoot.displayName = 'BuyOneGetOneShelf.Root'

// 创建命名空间对象，包含 Root 和 ProductCard 组件
const BuyOneGetOneShelf = {
  Root: BuyOneGetOneShelfRoot,
  ProductCard: ProductCard,
}

export default BuyOneGetOneShelf

// Re-export types for external use
export type {
  FreeGift,
  FreeGiftWithSelection,
  ProductTag,
  BuyOneGetOneShelfSemanticName,
  BuyOneGetOneShelfRootSemanticName,
  BuyOneGetOneShelfProductCardSemanticName,
  ResponsiveImage,
  ResponsiveImages,
  BaseProductData,
  PureProductData,
  WithGiftData,
  ProductData,
  ProductCardData,
  CopyConfig,
  BogoItem,
  BuyOneGetOneShelfData,
  BuyOneGetOneShelfProps,
  BuyOneGetOneShelfCardProps,
} from './types.js'
